import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { EnergyEfficiencySettingService, RealEstateSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { SessionService } from '../../../services/session.service';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

/** Welche uVI-Zustellarten der eingeloggte Benutzer an einer Bewohner-Rolle setzen darf. */
export interface UviReceptionRights {
	/** „Manuelle Zustellung" (PRINT) */
	canSetPrint: boolean;
	/** „Keine" (NO_EED) */
	canSetNone: boolean;
}

/** Voreinstellung, solange die Rechte nicht aufgelöst sind — im Zweifel nicht änderbar. */
export const NO_UVI_RECEPTION_RIGHTS: UviReceptionRights = { canSetPrint: false, canSetNone: false };

const ALL_UVI_RECEPTION_RIGHTS: UviReceptionRights = { canSetPrint: true, canSetNone: true };

/**
 * Löst die Liegenschafts-Einstellung „Ändern der manuellen Zustellung / auf keine Zustellung erlauben
 * für: Hausverwaltung / Eigentümer" (im Portal unter den uVI-Einstellungen der Liegenschaft) gegen die
 * Rolle des eingeloggten Benutzers auf. dev/support bleiben davon unberührt.
 */
@Injectable({ providedIn: 'root' })
export class UviReceptionRightsService {
	private readonly sessionService = inject(SessionService);
	private readonly energyEfficiencySettingService = inject(EnergyEfficiencySettingService);

	resolve(area: number, realEstateId: string | null): Observable<UviReceptionRights> {
		if (this.sessionService.isDevRole() || this.sessionService.isSupportRole()) {
			return of(ALL_UVI_RECEPTION_RIGHTS);
		}
		if (!realEstateId) {
			return of(NO_UVI_RECEPTION_RIGHTS);
		}
		return forkJoin([
			this.energyEfficiencySettingService.getRealEstateSetting(area, realEstateId),
			this.sessionService.getAllPermissionsOfLoggedInUser(),
		]).pipe(
			map(([setting, permissions]) => this.fromPermissions(permissions, setting)),
			// Ohne Einstellung oder Berechtigungen bleibt die Zustellart gesperrt.
			catchError(() => of(NO_UVI_RECEPTION_RIGHTS))
		);
	}

	/**
	 * Bewusst ohne Abgleich der `tenantUnitId`: die Berechtigungen führen Nutzeinheiten je nach Rollentyp
	 * mit interner oder Export-Id. Sichtbar ist eine Nutzeinheit ohnehin nur mit passender Berechtigung.
	 */
	private fromPermissions(permissions: PermissionObject[], setting: RealEstateSetting | null): UviReceptionRights {
		if (!setting) {
			return NO_UVI_RECEPTION_RIGHTS;
		}
		const isPropertyManagement = permissions.some((permission) => permission.permissionType === PermissionTypeEnum.PROPERTY_MANAGEMENT);
		const isOwner = permissions.some((permission) => permission.permissionType === PermissionTypeEnum.OWNER);
		return {
			canSetPrint: (isPropertyManagement && !!setting.pmAllowedManualDeliveryOption) || (isOwner && !!setting.ownerAllowedManualDeliveryOption),
			canSetNone: (isPropertyManagement && !!setting.pmAllowedNoneOption) || (isOwner && !!setting.ownerAllowedNoneOption),
		};
	}
}
